/**
 * What a stored rich-text value may keep — **a mirror of survey-api's `RichTextSanitizer.cs`**
 * (`src/Veritant.Surveys.Infrastructure/RichText/RichTextSanitizer.cs`, read at `c9113da9` on
 * `feature/SR-222-advisor-page`), and it **moves with survey-api**, the `COLUMN_DEFAULTS`
 * precedent: a list changed there is changed here in the same breath.
 *
 * survey-api runs that sanitiser on a staff field's write and strips rather than refuses; the
 * Salesforce archive (`…/salesforce-records`) is served verbatim, and its design puts the
 * sanitising on the client. So `RichText` runs this allowlist on **every** value (the F1 kit
 * plan's R7) — a staff field already clean comes out unchanged, an archived one comes out as
 * survey-api would have stored it — and the editor's loss check (R13) reads the same lists. One
 * list for both, so the renderer and the editor can never disagree about what a value holds.
 *
 * Nothing here judges a value: a value is never refused, only drawn without what the server
 * would not keep. The two patterns below are the server's rules, never a judgement of this side's:
 * the image test is its own (`InlineImage()`), mirrored so an archived value draws what a stored
 * one would hold, and `KEPT_HREF` is DOMPurify's own URI pattern narrowed to the three schemes
 * HtmlSanitizer keeps.
 */

/** `KeptTags` (lines 18–24). Any other element is unwrapped and its text kept. */
export const KEPT_TAGS: readonly string[] = [
  'p', 'br', 'div', 'span', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr',
  'a', 'img', 'table', 'caption', 'colgroup', 'col', 'thead', 'tbody', 'tfoot', 'tr', 'th',
  'td'
];

/**
 * `DroppedWithContent` (lines 26–32): removed with everything inside them, so a script's source
 * or a form's labels never surface as text.
 */
export const DROPPED_WITH_CONTENT: readonly string[] = [
  'script', 'style', 'iframe', 'frame', 'object', 'embed', 'noscript', 'template', 'svg',
  'math', 'form', 'input', 'button', 'select', 'textarea'
];

/** `AttributesByTag` (lines 36–45): what each element keeps besides `style`, which all keep. */
export const ATTRIBUTES_BY_TAG: Readonly<Record<string, readonly string[]>> = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'width', 'height', 'data-image-id'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan']
};

/**
 * `KeptCssProperties` (lines 47–53): the design's seven, plus the three longhands a CSS parser
 * splits `text-decoration` into — without which the shorthand never survives.
 */
export const KEPT_CSS_PROPERTIES: readonly string[] = [
  'color', 'background-color', 'font-size', 'font-weight', 'font-style', 'text-decoration',
  'text-decoration-line', 'text-decoration-style', 'text-decoration-color', 'text-align'
];

/**
 * `KeptSchemes` (line 55), as the pattern a link's `href` is tested against: one of the three
 * schemes, or no scheme at all — the sanitiser keeps a relative address, refusing only a scheme
 * outside the three. A refused `href` is removed and the link's text kept.
 */
export const KEPT_HREF = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/**
 * The `data-image-id` rule: the library item a picture was placed from, kept on an image and only
 * as a GUID, so a replacement in the media library can find the picture in the words.
 */
export const KEPT_IMAGE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `InlineImage()` (lines 161–162): an inline raster image. */
const INLINE_IMAGE = /^data:image\/(png|jpeg|gif|webp);base64,[A-Za-z0-9+/\s]*=*\s*$/i;

/**
 * `IsOrgImage` (lines 154–159): an image the org's rich-text servlet serves — `https`, a host
 * under `force.com` or `salesforce.com`, path `/servlet/rtaImage`, case-insensitive. Block 8 of
 * survey-api inlines these as `data:`; until it has run on a stand, a value still carries them.
 */
export function isOrgImage(src: string): boolean {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }
  const host = url.hostname.toLowerCase();
  return (
    url.protocol === 'https:' &&
    (host.endsWith('.force.com') || host.endsWith('.salesforce.com')) &&
    url.pathname.toLowerCase() === '/servlet/rtaimage'
  );
}

/**
 * `IsKeptImage` (lines 151–152), Q3's image rule: an inline raster image or an org image. Any
 * other `img` is removed whole — a remote picture would otherwise load from wherever the value
 * points.
 */
export function isKeptImage(src: string): boolean {
  return INLINE_IMAGE.test(src) || isOrgImage(src);
}

/**
 * An image fetched over the web, as opposed to one carried inside the value.
 *
 * It is **not** part of the allowlist: by itself it admits a picture from anywhere, which is
 * precisely what `isKeptImage` refuses. It exists for the one caller that has already been told
 * the value's images were judged by survey-api — see `RichText`'s `serverResolvedImages`.
 */
export function isHttpImage(src: string): boolean {
  return /^https?:\/\//i.test(src);
}
